import {
	type BaseFormField as SchemaBaseFormField,
	type FormField as SchemaFormField,
	type FormFieldLine as SchemaFormFieldLine,
	type FormFieldLineTypeOptions as SchemaFormFieldLineTypeOptions,
	type FormFieldNestedList as SchemaFormFieldNestedList,
	type FormFieldNestedListOptions,
	type FormFieldNestedListOptions as SchemaFormFieldNestedListOptions,
	type FormFieldNestedListValues as SchemaFormFieldNestedListValues,
	type FormFieldSelectList as SchemaFormFieldSelectList,
	type FormFieldSubForm as SchemaFormFieldSubForm,
	type FormFieldSubFormSchema as SchemaFormFieldSubFormSchema,
	type FormMetadata as SchemaFormMetadata,
} from '@ikegps/core-api';
import { IkeFieldType } from '@ikegps/core-api';
import type { FormModelField } from '../form.field';
import type { FormModelMetadata } from '../form.metadata';
import type { FormModelFieldLine } from '../form.line';
import type { FormModelFieldSelectList } from '../form.select.list';
import type { FormModelFieldSubForm } from '../subform/form.subform';
import type { FormModelFieldNestedList } from '../nested.list/form.nested.list';

const writeFields = (fields: FormModelField[]): SchemaFormField[] => fields.map((field) => FieldWriter.write(field));

const BaseFieldWriter = {
	write(field: FormModelField): SchemaBaseFormField {
		const fieldJSON: SchemaBaseFormField = {
			name: field.id,
			displayname: field.displayName,
			type: field.type,
		};

		if (field.title != null) {
			fieldJSON.title = field.title;
		}

		if (field.subtitle != null) {
			fieldJSON.subtitle = field.subtitle;
		}

		if (field.adminOnly != null) {
			fieldJSON.adminOnly = field.adminOnly;
		}

		if (field.annotationLabelFieldId != null) {
			fieldJSON.annotationLabelFieldId = field.annotationLabelFieldId;
		}

		if (field.clearanceThresholdInMeters != null) {
			fieldJSON.clearanceThresholdInMeters = field.clearanceThresholdInMeters;
		}

		if (field.cloneable != null) {
			fieldJSON.cloneable = field.cloneable;
		}

		if (Array.isArray(field.providers) && field.providers.length > 0) {
			fieldJSON.providers = field.providers;
		}

		if (field.collector != null) {
			fieldJSON.collector = field.collector;
		}

		if (field.required != null) {
			fieldJSON.required = field.required;
		}

		if (field.stateValue.locked != null) {
			fieldJSON.locked = field.stateValue.locked;
		}

		if (field.stateValue.isGenerated != null) {
			fieldJSON.isGenerated = field.stateValue.isGenerated;
		}

		return fieldJSON;
	},
};

const SubFormWriter = {
	TYPE: IkeFieldType.FORM,
	write(field: FormModelFieldSubForm): SchemaFormFieldSubForm {
		const fieldJSON = <SchemaFormFieldSubForm>BaseFieldWriter.write(field);
		fieldJSON.schema = {
			fields: writeFields(field.fields),
			label: undefined,
			subformType: field.subFormType,
			subformSpanTypeState: field.stateValue.spanTypeState ?? null,
			subformAnchorTypeState: field.stateValue.anchorTypeState ?? null,
			subformEquipmentTypeState: field.stateValue.equipmentTypeState ?? null,
		};
		if (Array.isArray(field.label) && field.label.length > 0) {
			fieldJSON.schema.label = field.label;
		}
		return fieldJSON;
	},
};

const NestedListWriter = {
	TYPE: IkeFieldType.NESTED_LIST,
	write(field: FormModelFieldNestedList): SchemaFormFieldNestedList {
		const fieldJSON = <SchemaFormFieldNestedList>BaseFieldWriter.write(field);
		fieldJSON.options = this.writeOptions(field.options);
		return fieldJSON;
	},
	writeOptions(options: FormFieldNestedListOptions): SchemaFormFieldNestedListOptions {
		if (options == null) {
			return null;
		}

		return {
			title: options.title,
			values: options.values.map((value) => {
				const valueJSON: SchemaFormFieldNestedListValues = {
					value: value.value,
				};

				if (value.code != null) {
					valueJSON.code = value.code;
				}

				if (value.color != null) {
					valueJSON.color = value.color;
				}

				if (value.options != null) {
					valueJSON.options = this.writeOptions(value.options);
				}

				return valueJSON;
			}),
		};
	},
};

const SelectListWriter = {
	TYPE: IkeFieldType.SELECT_LIST,
	write(field: FormModelFieldSelectList): SchemaFormFieldSelectList {
		const fieldJSON = <SchemaFormFieldSelectList>BaseFieldWriter.write(field);

		fieldJSON.options = field.options.map(({ text, value, color }) => ({ text, value, color }));

		return fieldJSON;
	},
};

const LineWriter = {
	TYPE: IkeFieldType.LINE,
	write(field: FormModelFieldLine): SchemaFormFieldLine {
		const fieldJSON = <SchemaFormFieldLine>BaseFieldWriter.write(field);
		fieldJSON.lineTypes = field.lineTypes.map(
			({ symbolId, title, visible, symbolColor }): SchemaFormFieldLineTypeOptions => ({
				symbolId,
				title,
				visible,
				symbolColor,
			})
		);
		return fieldJSON;
	},
};

const FieldWriter = {
	writers: {
		[SubFormWriter.TYPE]: SubFormWriter,
		[NestedListWriter.TYPE]: NestedListWriter,
		[SelectListWriter.TYPE]: SelectListWriter,
		[LineWriter.TYPE]: LineWriter,
		DEFAULT: BaseFieldWriter,
	},
	getWriter(type) {
		return this.writers[type] || this.writers.DEFAULT;
	},
	write(field: FormModelField): SchemaFormField {
		return this.getWriter(field.type).write(field);
	},
};

export const writeSchema = (form: FormModelFieldSubForm): SchemaFormFieldSubFormSchema => {
	return {
		fields: writeFields(form.fields),
		subformType: form.stateValue.subFormType ?? null,
		subformSpanTypeState: form.stateValue.spanTypeState ?? null,
		subformAnchorTypeState: form.stateValue.anchorTypeState ?? null,
		subformEquipmentTypeState: form.stateValue.equipmentTypeState ?? null,
	};
};

export const writeMetadata = (metadata: FormModelMetadata, fields: FormModelField[]): SchemaFormMetadata => {
	if (metadata === null || Object.keys(metadata).length === 0) {
		return undefined;
	}

	const metadataJSON = JSON.parse(JSON.stringify(metadata.options)) as SchemaFormMetadata;
	const emptyJSON = JSON.parse('{}') as SchemaFormMetadata;

	if (metadata.import && metadata.import.type != null && metadata.import.version != null) {
		metadataJSON.import = {
			type: metadata.import.type,
			version: metadata.import.version,
		};
	}

	const statusFieldId = metadata.statusFieldId;
	if (statusFieldId != null) {
		metadataJSON.status = { field: statusFieldId };
	}

	const mapColorFieldId = metadata.mapColorFieldId;
	if (mapColorFieldId != null) {
		metadataJSON.mapColor = { field: mapColorFieldId };
	}

	const addressLookupFieldId = metadata.addressLookupFieldId;
	if (addressLookupFieldId != null) {
		metadataJSON.address_lookup = { address: { field: addressLookupFieldId } };
	} else {
		delete metadataJSON.address_lookup;
	}

	if (JSON.stringify(metadataJSON).localeCompare(JSON.stringify(emptyJSON)) === 0) {
		return undefined;
	}

	return metadataJSON;
};
